import React from 'react';
import clsx from 'clsx';
import profileStyles from './UserProfile.module.scss';
import tabStyles from '../../../../ManagementProfileTab/ManagementProfileTab.module.scss';
import { resolveDirectorLegalCases } from '../../../../ManagementProfileTab/checkStatusResolvers';
import { LegalCasesStateLabel, NotConfiguredLabel } from '../../../../ManagementProfileTab/CheckStatusLabels';
import { NOT_CONFIGURED_TOOLTIP } from '../../../../ManagementProfileTab/ManagementProfileTabConstants';
import type { DirectorLegalCases } from '../../../../ManagementProfileTab/types';
import { LEGAL_DIRECTION } from '../../../../../components/DashboardTables/LegalCases/legalCasesConstants';
import { RISK_HEADER_VALUES } from '../../../../../../RiskIndicators/RiskIndicatorConstants';
import type { Direction } from '../../../../../components/DashboardTables/LegalCases/legalCasesType';
import DrawerRow from './DrawerRow';
import ClickableValue from './ClickableValue';
import RegulatoryCountRow from './RegulatoryCountRow';

const LABEL_BY_DIRECTION: Record<Direction, string> = {
    [LEGAL_DIRECTION.FILED_AGAINST]: RISK_HEADER_VALUES.LEGAL_CASES,
    [LEGAL_DIRECTION.FILED_BY]: RISK_HEADER_VALUES.LEGAL_CASES_BY,
};

interface LegalCasesRowProps {
    direction: Direction;
    legalCases?: DirectorLegalCases | null;
    // Filed Against's old summed count, shown only while polus does not send legalCases.
    legacyCount?: number | null;
    notReady?: boolean;
    notConfigured?: boolean;
    onClick?: () => void;
    isPDFReport?: boolean;
}

/** One direction of the director's Legal Cases, drawn exactly as the Management Profile table cell. */
const LegalCasesRow = ({ direction, legalCases, legacyCount, notReady, notConfigured, onClick, isPDFReport }: LegalCasesRowProps) => {
    const label = LABEL_BY_DIRECTION[direction];
    // Not Configured comes first: it is decided by config, whatever result data did or did not arrive.
    if (notConfigured) {
        return <DrawerRow label={label} valueOwnsStyling title={NOT_CONFIGURED_TOOLTIP} value={<NotConfiguredLabel />} />;
    }
    // An older polus sends no Filed By data and the table has no Filed By column yet, so neither does the drawer.
    if (legalCases === undefined && direction === LEGAL_DIRECTION.FILED_BY) return null;
    if (legalCases === undefined && direction === LEGAL_DIRECTION.FILED_AGAINST) {
        return (
            <RegulatoryCountRow
                label={label}
                count={legacyCount}
                notReady={notReady}
                unitSingular="Case"
                unitPlural="Cases"
                zeroLabel="No match found"
                onClickIfPositive={onClick}
                isPDFReport={isPDFReport}
            />
        );
    }
    const state = notReady ? null : resolveDirectorLegalCases({ legalCases }, direction);
    if (!state) return <DrawerRow label={label} value={undefined} />;
    const clickable = state.openable && !isPDFReport && !!onClick;
    return (
        <DrawerRow
            label={label}
            valueOwnsStyling
            title={state.tooltip}
            value={
                <ClickableValue
                    clickable={clickable}
                    onClick={onClick}
                    className={clsx(profileStyles.CountValue, { [tabStyles.alertClickable]: clickable })}
                >
                    <LegalCasesStateLabel state={state} inline showIcon={!isPDFReport} />
                </ClickableValue>
            }
        />
    );
};

export default LegalCasesRow;
